import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  WireframeLayout,
  WireframeHeader,
  WireframeContent,
  WireframeTabs,
  WireframeCard,
  WireframeButton,
  WireframeTable,
  StatCard,
  StatusIndicator,
  CrisisBadge,
} from './WireframeLayout';
import {
  Skeleton,
  SkeletonTable,
  LoadingSpinner,
  Modal,
  Select,
  Input,
  TextArea,
  useToast,
  EmptyState,
  ConfirmDialog,
  HelpTooltip,
  RefreshBadge,
} from './WireframeUI';
import { TutorialStartButton } from './WireframeTutorial';
import { Search, Plus, Check, Bell, Database, Filter, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { wireframeMockApi, Alert, DataSource, crisisTypes, regions, severityLevels } from '../../services/wireframeMockApi';
import { dataSourceService, EWASAlert, EWASSourceId } from '../../services/nrc-ewas';
import { mockEWASSources, getMockEWASRecords } from '../../data/ewasData';
import { getAllNRCCountries, getRegionsForCountry } from '../../data/situationData';

// FilterDropdown component
function FilterDropdown({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => options.length > 0 && setIsOpen(!isOpen)}
        className="bg-white border border-gray-200 px-3 py-1.5 min-w-[120px] cursor-pointer hover:border-gray-300"
      >
        <div className="text-[9px] text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-[12px] font-semibold flex items-center justify-between gap-2">
          {value}{' '}
          <ChevronDown className={cn("w-3 h-3 text-gray-400 transition-transform", isOpen && "rotate-180")} />
        </div>
      </div>

      {isOpen && options.length > 0 && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-lg z-50 min-w-[160px] max-h-[300px] overflow-y-auto">
          {options.map((option) => (
            <div
              key={option}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className={cn(
                "px-3 py-2 text-sm cursor-pointer hover:bg-gray-50",
                value === option && "bg-gray-100 font-medium"
              )}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Date options for the dropdown
const dateOptions = [
  'Feb 2026',
  'Jan 2026',
  'Dec 2025',
  'Nov 2025',
  'Oct 2025',
  'Last 7 days',
  'Last 30 days',
  'Last 90 days',
];

// EWAS source filter options
const ewasSourceFilters: { id: EWASSourceId | 'all'; label: string; color: string }[] = [
  { id: 'all', label: 'All Sources', color: 'bg-gray-500' },
  { id: 'acled', label: 'ACLED', color: 'bg-red-800' },
  { id: 'dataminr', label: 'Dataminr', color: 'bg-amber-500' },
  { id: 'iom_dtm', label: 'IOM DTM', color: 'bg-purple-600' },
  { id: 'fewsnet', label: 'FEWSNET', color: 'bg-orange-600' },
  { id: 'glofas', label: 'GloFAS', color: 'bg-blue-500' },
];

export function WireframeDetection() {
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === 'true';

  // Notify parent window of current page for tooltip coordination
  useEffect(() => {
    if (isEmbedded && window.parent !== window) {
      window.parent.postMessage({ type: 'CLEAR_DEMO_PAGE', page: 'detection' }, '*');
    }
  }, [isEmbedded]);

  const [activeTab, setActiveTab] = useState(0);
  const [activeFilter, setActiveFilter] = useState<'cholera' | 'flooding' | 'drought'>('cholera');
  const [highlightedCrisis, setHighlightedCrisis] = useState<string | null>(null);
  const [ewasSourceFilter, setEwasSourceFilter] = useState<EWASSourceId | 'all'>('all');
  const [showEWASAlerts, setShowEWASAlerts] = useState(true);

  // Location and date filters
  const [selectedCountry, setSelectedCountry] = useState('Sudan');
  const [selectedRegion, setSelectedRegion] = useState('All Regions');
  const [selectedDate, setSelectedDate] = useState('Feb 2026');

  const allCountries = getAllNRCCountries();
  const regionsList = getRegionsForCountry(selectedCountry);

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    setSelectedRegion('All Regions');
  };

  // Data states
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [ewasAlerts, setEwasAlerts] = useState<EWASAlert[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [stats, setStats] = useState<{ activeCrises: number; dataSourcesOnline: number; alertsToday: number; ewasRecords?: number } | null>(null);

  // Loading states
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [loadingEWASAlerts, setLoadingEWASAlerts] = useState(true);
  const [loadingDataSources, setLoadingDataSources] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);

  // Last updated timestamps
  const [alertsLastUpdated, setAlertsLastUpdated] = useState<Date | null>(null);
  const [dataSourcesLastUpdated, setDataSourcesLastUpdated] = useState<Date | null>(null);

  // Modal states
  const [showCreateAlert, setShowCreateAlert] = useState(false);
  const [creatingAlert, setCreatingAlert] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Map refs
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  // Fallback token for wireframe demos
  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1Ijoic2FtZnJvbnMiLCJhIjoiY20wcXprNHFmMDM5dDJpb3A3czEyYXU2OCJ9.z7lv5-q6CZW8Xq59kn1bYA';

  // Toast
  const { addToast } = useToast();

  const tabs = [
    { label: 'Live Alerts', active: activeTab === 0 },
    { label: 'Data Sources', active: activeTab === 1 },
    { label: 'Alert Rules', active: activeTab === 2 },
    { label: 'History', active: activeTab === 3 },
  ];

  // Load data on mount
  useEffect(() => {
    loadAlerts();
    loadEWASAlerts();
    loadDataSources();
    loadStats();
  }, []);

  const loadEWASAlerts = async () => {
    setLoadingEWASAlerts(true);
    try {
      const alerts = await dataSourceService.getAlerts();
      setEwasAlerts(alerts);
    } catch (error) {
      console.error('Failed to load EWAS alerts');
    } finally {
      setLoadingEWASAlerts(false);
    }
  };

  const loadAlerts = async () => {
    setLoadingAlerts(true);
    try {
      const data = await wireframeMockApi.getAlerts();
      setAlerts(data);
      setAlertsLastUpdated(new Date());
    } catch (error) {
      addToast({ type: 'error', title: 'Failed to load alerts' });
    } finally {
      setLoadingAlerts(false);
    }
  };

  const loadDataSources = async () => {
    setLoadingDataSources(true);
    try {
      const data = await wireframeMockApi.getDataSources();
      setDataSources(data);
      setDataSourcesLastUpdated(new Date());
    } catch (error) {
      addToast({ type: 'error', title: 'Failed to load data sources' });
    } finally {
      setLoadingDataSources(false);
    }
  };

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const [data, pipelineStats] = await Promise.all([
        wireframeMockApi.getStats(),
        wireframeMockApi.getPipelineStats(),
      ]);
      setStats({
        ...data,
        dataSourcesOnline: pipelineStats.sourcesOnline,
        ewasRecords: pipelineStats.ewasRecords,
      });
    } catch (error) {
      console.error('Failed to load stats');
    } finally {
      setLoadingStats(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    try {
      await wireframeMockApi.acknowledgeAlert(alertId);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, acknowledged: true } : a));
      addToast({ type: 'success', title: 'Alert acknowledged', message: 'The alert has been marked as reviewed.' });
    } catch (error) {
      addToast({ type: 'error', title: 'Failed to acknowledge alert' });
    }
  };

  // Filter alerts by search query
  const filteredAlerts = alerts.filter(alert => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      alert.title.toLowerCase().includes(query) ||
      alert.region.toLowerCase().includes(query) ||
      alert.locations.toLowerCase().includes(query)
    );
  });

  // Filter EWAS alerts by search query and source
  const filteredEWASAlerts = ewasAlerts.filter(alert => {
    // Filter by source
    if (ewasSourceFilter !== 'all' && alert.source !== ewasSourceFilter) {
      return false;
    }
    // Filter by search query
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      alert.title.toLowerCase().includes(query) ||
      alert.region.toLowerCase().includes(query) ||
      alert.locations.toLowerCase().includes(query) ||
      alert.sourceName.toLowerCase().includes(query)
    );
  });

  // Combined alerts for display (CLEAR alerts + EWAS alerts)
  const combinedAlerts = showEWASAlerts
    ? [...filteredAlerts, ...filteredEWASAlerts.map(ea => ({
        id: ea.id,
        type: ea.source === 'acled' || ea.source === 'acled_cast' ? 'conflict' as const :
              ea.source === 'iom_dtm' || ea.source === 'dtm_flash' ? 'displacement' as const :
              ea.source === 'fewsnet' ? 'drought' as const :
              ea.source === 'glofas' ? 'flooding' as const : 'cholera' as const,
        title: ea.title,
        severity: ea.severity,
        region: ea.region,
        locations: ea.locations,
        window: ea.window || '24h',
        confidence: ea.confidence || '85%',
        timestamp: new Date(ea.timestamp).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' ago',
        description: ea.description,
        source: ea.sourceName,
        acknowledged: ea.acknowledged,
        isEWAS: true,
        ewasSource: ea.source,
      }))]
    : filteredAlerts;

  // Initialize map
  useEffect(() => {
    // Prevent double init
    if (mapRef.current) {
      return;
    }

    const container = document.getElementById('wireframe-detection-map');
    if (!container) {
      return;
    }

    mapboxgl.accessToken = mapboxToken;

    const map = new mapboxgl.Map({
      container: 'wireframe-detection-map',
      style: 'mapbox://styles/mapbox/light-v11',
      center: [42.0, 8.0],
      zoom: 5,
    });

    mapRef.current = map;

    // Don't clean up - let it persist
  }, []);

  // Update markers when filter changes
  useEffect(() => {
    if (!mapRef.current || loadingAlerts) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    const map = mapRef.current;
    const selectedAlert = alerts.find((a) => a.type === activeFilter);

    if (selectedAlert) {
      const colors: Record<string, string> = {
        critical: '#DC2626',
        high: '#F59E0B',
        medium: '#D97706',
      };

      const coordinates: [number, number][] = activeFilter === 'cholera'
        ? [[42.79, 9.35], [44.2, 6.73]]
        : activeFilter === 'flooding'
        ? [[39.76, 7.0]]
        : [[40.0, 11.5]];

      const bounds = new mapboxgl.LngLatBounds();

      coordinates.forEach((coord) => {
        const el = document.createElement('div');
        el.style.cssText = `
          width: 16px;
          height: 16px;
          background: ${colors[selectedAlert.severity]};
          cursor: pointer;
          position: relative;
        `;

        if (selectedAlert.severity === 'critical') {
          const pulse = document.createElement('div');
          pulse.style.cssText = `
            position: absolute;
            top: -4px;
            left: -4px;
            right: -4px;
            bottom: -4px;
            background: rgba(220, 38, 38, 0.3);
            animation: pulse-ring 2s infinite;
          `;
          el.appendChild(pulse);
        }

        const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
          <div style="padding: 8px; font-family: Inter, sans-serif;">
            <div style="font-weight: 600; font-size: 14px; margin-bottom: 4px;">${selectedAlert.title}</div>
            <div style="font-size: 12px; color: #6B7280;">${selectedAlert.region}</div>
            <div style="font-size: 12px; color: #6B7280;">Action window: ${selectedAlert.window}</div>
            <span style="display: inline-block; padding: 2px 8px; font-size: 10px; font-weight: 600; margin-top: 8px; background: ${selectedAlert.severity === 'critical' ? '#FEE2E2' : '#FEF3C7'}; color: ${colors[selectedAlert.severity]};">${selectedAlert.severity.charAt(0).toUpperCase() + selectedAlert.severity.slice(1)}</span>
          </div>
        `);

        const marker = new mapboxgl.Marker(el)
          .setLngLat(coord)
          .setPopup(popup)
          .addTo(map);

        markersRef.current.push(marker);
        bounds.extend(coord);
      });

      map.fitBounds(bounds, { padding: 80, maxZoom: 7 });
    }
  }, [activeFilter, alerts, loadingAlerts]);

  return (
    <WireframeLayout embedded={isEmbedded}>
      {!isEmbedded && <WireframeHeader
        title="Crisis Detection"
        breadcrumbs={[
          { label: 'CLEAR', href: '/wireframes' },
          { label: 'Detection' }
        ]}
        actions={
          <>
            <FilterDropdown
              label="Country"
              value={selectedCountry}
              options={allCountries}
              onChange={handleCountryChange}
            />
            <FilterDropdown
              label="Region"
              value={selectedRegion}
              options={regionsList}
              onChange={setSelectedRegion}
            />
            <FilterDropdown
              label="Date"
              value={selectedDate}
              options={dateOptions}
              onChange={setSelectedDate}
            />
            <TutorialStartButton tutorialId="detection" variant="icon" />
            <div className="relative" data-tutorial="search">
              <input
                type="text"
                placeholder="Search alerts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-[200px] px-3 py-2 pl-9 border border-gray-200 bg-white text-[13px] outline-none focus:border-accent-500"
              />
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
            <div data-tutorial="create-alert">
              <WireframeButton
                variant="primary"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setShowCreateAlert(true)}
              >
                Create Manual Alert
              </WireframeButton>
            </div>
          </>
        }
      />}

      <WireframeContent>
        <WireframeTabs tabs={tabs} onTabClick={setActiveTab} />

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
          {loadingStats ? (
            <>
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="bg-white border border-gray-200 p-5">
                  <Skeleton className="h-3 w-1/2 mb-3" />
                  <Skeleton className="h-8 w-1/3" />
                </div>
              ))}
            </>
          ) : (
            <>
              <StatCard label="Active Alerts" value={stats?.activeCrises ?? 0} valueColor="text-red-600" />
              <StatCard label="Data Sources" value={`${stats?.dataSourcesOnline ?? 0}/18`} valueColor="text-green-600" />
              <StatCard label="Events Today" value={stats?.alertsToday ?? 0} />
              <StatCard label="EWAS Records" value={stats?.ewasRecords ? `${(stats.ewasRecords / 1000).toFixed(1)}k` : '55.4k'} valueColor="text-accent-500" />
              <StatCard label="Avg Response" value="2.4h" />
            </>
          )}
        </div>

        {activeTab === 0 && (
          /* Two Column: Alerts + Map */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Active Alerts List */}
            <div data-tutorial="alert-list">
            <WireframeCard
              title={
                <span className="flex items-center gap-2">
                  Active Alerts ({combinedAlerts.length})
                  <HelpTooltip content="Alerts from CLEAR system and NRC EWAS Sudan data pipeline (ACLED, IOM DTM, FEWSNET, Dataminr)" />
                </span>
              }
              subtitle="CLEAR + EWAS Sudan data"
              headerAction={
                <div className="flex items-center gap-2">
                  {/* EWAS Toggle */}
                  <button
                    onClick={() => setShowEWASAlerts(!showEWASAlerts)}
                    className={cn(
                      'flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold border transition-colors',
                      showEWASAlerts
                        ? 'bg-accent-50 border-accent-200 text-accent-600'
                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                    )}
                  >
                    <Database className="w-3 h-3" />
                    EWAS
                  </button>
                  <RefreshBadge
                    lastUpdated={alertsLastUpdated}
                    onRefresh={() => { loadAlerts(); loadEWASAlerts(); }}
                    isRefreshing={loadingAlerts || loadingEWASAlerts}
                  />
                </div>
              }
              noPadding
            >
              {/* EWAS Source Filter Pills */}
              {showEWASAlerts && (
                <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap gap-1.5">
                  {ewasSourceFilters.map((filter) => (
                    <button
                      key={filter.id}
                      onClick={() => setEwasSourceFilter(filter.id)}
                      className={cn(
                        'flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-medium border transition-colors',
                        ewasSourceFilter === filter.id
                          ? 'bg-gray-900 border-gray-900 text-white'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      )}
                    >
                      <span className={cn('w-1.5 h-1.5', filter.color)} />
                      {filter.label}
                    </button>
                  ))}
                </div>
              )}
              {loadingAlerts || loadingEWASAlerts ? (
                <div className="p-5 space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex gap-4">
                      <Skeleton className="w-1 h-16" />
                      <div className="flex-1">
                        <Skeleton className="h-3 w-1/4 mb-2" />
                        <Skeleton className="h-4 w-2/3 mb-2" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : combinedAlerts.length === 0 ? (
                <EmptyState
                  variant={searchQuery ? 'no-results' : 'no-data'}
                  title={searchQuery ? 'No alerts found' : 'No active alerts'}
                  message={searchQuery ? `No alerts match "${searchQuery}". Try adjusting your search terms.` : 'When new crises are detected, alerts will appear here.'}
                  action={searchQuery ? { label: 'Clear search', onClick: () => setSearchQuery('') } : undefined}
                  className="py-8"
                />
              ) : (
                combinedAlerts.map((alert: any) => (
                  <div
                    key={alert.id}
                    className={cn(
                      'border-b border-gray-200 last:border-b-0 transition-colors',
                      highlightedCrisis === alert.id && 'bg-gray-50 shadow-[inset_3px_0_0_0_theme(colors.accent.500)]'
                    )}
                    onMouseEnter={() => setHighlightedCrisis(alert.id)}
                    onMouseLeave={() => setHighlightedCrisis(null)}
                  >
                    <div className="flex items-start gap-4 p-5">
                      <div
                        className={cn(
                          'w-1 h-12 flex-shrink-0',
                          alert.severity === 'critical' && 'bg-red-600',
                          alert.severity === 'high' && 'bg-amber-500',
                          alert.severity === 'medium' && 'bg-amber-400',
                          alert.severity === 'low' && 'bg-gray-400'
                        )}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <CrisisBadge severity={alert.severity} />
                          {/* EWAS Source Badge */}
                          {alert.isEWAS && (
                            <span className={cn(
                              'text-[9px] font-bold uppercase px-1.5 py-0.5 text-white',
                              alert.ewasSource === 'acled' || alert.ewasSource === 'acled_cast' ? 'bg-red-800' :
                              alert.ewasSource === 'dataminr' ? 'bg-amber-500' :
                              alert.ewasSource === 'iom_dtm' || alert.ewasSource === 'dtm_flash' ? 'bg-purple-600' :
                              alert.ewasSource === 'fewsnet' ? 'bg-orange-600' :
                              alert.ewasSource === 'glofas' ? 'bg-blue-500' : 'bg-gray-500'
                            )}>
                              {alert.source}
                            </span>
                          )}
                          <span className="text-[11px] text-gray-500">{alert.timestamp}</span>
                          {alert.acknowledged && (
                            <span className="text-[10px] text-green-600 flex items-center gap-1">
                              <Check className="w-3 h-3" /> Acknowledged
                            </span>
                          )}
                        </div>
                        <div className="text-sm font-semibold text-gray-900">{alert.title}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {alert.region} • {alert.locations}
                        </div>
                        {alert.description && (
                          <div className="text-xs text-gray-600 mt-2 line-clamp-2">{alert.description}</div>
                        )}
                        <div className="flex gap-4 mt-3">
                          <div className="text-[11px]">
                            <span className="text-gray-500">Window: </span>
                            <span className="font-semibold">{alert.window}</span>
                          </div>
                          <div className="text-[11px]">
                            <span className="text-gray-500">Confidence: </span>
                            <span className="font-semibold">{alert.confidence}</span>
                          </div>
                        </div>
                        {!alert.acknowledged && !alert.isEWAS && (
                          <button
                            onClick={() => handleAcknowledgeAlert(alert.id)}
                            className="mt-3 text-xs text-accent-500 font-medium hover:text-accent-600"
                            data-tutorial="acknowledge"
                          >
                            Acknowledge Alert →
                          </button>
                        )}
                        {alert.isEWAS && (
                          <div className="mt-3 text-xs text-gray-400">
                            Via NRC EWAS Sudan Pipeline
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </WireframeCard>
            </div>

            {/* Crisis Map */}
            <WireframeCard
              title="Crisis Map"
              headerAction={
                <div className="flex gap-2" data-tutorial="filters">
                  {(['cholera', 'flooding', 'drought'] as const).map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setActiveFilter(filter)}
                      className={cn(
                        'px-3 py-1.5 text-xs font-medium border transition-colors',
                        activeFilter === filter
                          ? filter === 'cholera'
                            ? 'bg-red-50 border-red-200 text-red-600'
                            : filter === 'flooding'
                            ? 'bg-amber-50 border-amber-200 text-amber-600'
                            : 'bg-amber-50 border-amber-200 text-amber-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      )}
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </button>
                  ))}
                </div>
              }
              noPadding
            >
              <div ref={mapContainerRef} id="wireframe-detection-map" className="h-[360px] w-full" />
            </WireframeCard>
          </div>
        )}

        {activeTab === 1 && (
          /* Data Sources Tab */
          <div className="space-y-4">
            {/* EWAS Sudan Pipeline Sources */}
            <WireframeCard
              title={
                <span className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-accent-500" />
                  NRC EWAS Sudan Pipeline
                  <HelpTooltip content="NRC Early Warning Analysis System - 10 data sources with 55,426 records for Sudan crisis monitoring" />
                </span>
              }
              subtitle="10 sources • 55,426 records"
              headerAction={
                <span className="text-[10px] font-bold uppercase px-2 py-1 bg-green-100 text-green-700">
                  CONNECTED
                </span>
              }
              noPadding
            >
              <WireframeTable
                columns={[
                  { key: 'source', label: 'Source' },
                  { key: 'type', label: 'Type' },
                  { key: 'status', label: 'Status' },
                  { key: 'lastUpdate', label: 'Last Update' },
                  { key: 'records', label: 'Records' },
                ]}
                data={mockEWASSources.map((source) => ({
                  source: (
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        'w-2 h-2',
                        source.category === 'conflict' && 'bg-red-800',
                        source.category === 'displacement' && 'bg-purple-600',
                        source.category === 'food_security' && 'bg-orange-600',
                        source.category === 'flood' && 'bg-blue-500',
                        source.category === 'social' && 'bg-amber-500',
                        source.category === 'reports' && 'bg-gray-500',
                      )} />
                      <strong>{source.shortName}</strong>
                    </div>
                  ),
                  type: source.description.split(' - ')[0],
                  status: <StatusIndicator status={source.status === 'online' ? 'online' : source.status === 'syncing' ? 'delayed' : 'offline'} label={source.status.charAt(0).toUpperCase() + source.status.slice(1)} />,
                  lastUpdate: new Date(source.lastUpdated).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
                  records: source.recordCount.toLocaleString(),
                }))}
              />
            </WireframeCard>

            {/* Original CLEAR Data Sources */}
            <WireframeCard
              title={
                <span className="flex items-center gap-2">
                  CLEAR Data Sources
                  <HelpTooltip content="Core data sources feeding the CLEAR detection engine" />
                </span>
              }
              subtitle="8 sources feeding the detection engine"
              headerAction={
                <div className="flex items-center gap-2">
                  <RefreshBadge
                    lastUpdated={dataSourcesLastUpdated}
                    onRefresh={loadDataSources}
                    isRefreshing={loadingDataSources}
                  />
                  <WireframeButton size="sm">+ Connect Source</WireframeButton>
                </div>
              }
              noPadding
            >
              {loadingDataSources ? (
                <SkeletonTable rows={8} cols={5} />
              ) : (
                <WireframeTable
                  columns={[
                    { key: 'source', label: 'Source' },
                    { key: 'type', label: 'Type' },
                    { key: 'status', label: 'Status' },
                    { key: 'lastUpdate', label: 'Last Update' },
                    { key: 'events', label: 'Events (24h)' },
                  ]}
                  data={dataSources.map((source) => ({
                    source: <strong>{source.name}</strong>,
                    type: source.type,
                    status: <StatusIndicator status={source.status} label={source.status === 'online' ? 'Online' : source.status === 'delayed' ? 'Delayed' : 'Offline'} />,
                    lastUpdate: source.lastUpdate,
                    events: source.events.toLocaleString(),
                  }))}
                />
              )}
            </WireframeCard>
          </div>
        )}

        {activeTab === 2 && (
          /* Alert Rules Tab */
          <div data-tutorial="alert-rules">
          <WireframeCard title="Alert Rules" subtitle="Configure automatic alert triggers">
            <div className="space-y-4">
              <AlertRuleItem
                name="Cholera Case Threshold"
                description="Trigger alert when confirmed cases exceed 50 in a 48h period"
                enabled={true}
              />
              <AlertRuleItem
                name="Flood Warning Level"
                description="Trigger alert when river levels reach warning threshold"
                enabled={true}
              />
              <AlertRuleItem
                name="Drought NDVI Index"
                description="Trigger alert when NDVI drops below 0.2 for 30+ days"
                enabled={true}
              />
              <AlertRuleItem
                name="Displacement Movement"
                description="Trigger alert when IDP movement exceeds 5,000 in 24h"
                enabled={false}
              />
              <WireframeButton variant="primary" icon={<Plus className="w-4 h-4" />} className="mt-4">
                Add New Rule
              </WireframeButton>
            </div>
          </WireframeCard>
          </div>
        )}

        {activeTab === 3 && (
          /* History Tab */
          <WireframeCard title="Alert History" subtitle="Past 30 days">
            <div className="space-y-3">
              <HistoryItem
                title="Cholera Outbreak - Somali Region"
                date="Nov 15, 2025"
                status="Active"
                severity="critical"
              />
              <HistoryItem
                title="Flooding Risk - Oromia Region"
                date="Nov 14, 2025"
                status="Active"
                severity="high"
              />
              <HistoryItem
                title="Drought Monitoring - Afar Region"
                date="Nov 10, 2025"
                status="Monitoring"
                severity="medium"
              />
              <HistoryItem
                title="Displacement Alert - Tigray"
                date="Nov 5, 2025"
                status="Resolved"
                severity="high"
              />
              <HistoryItem
                title="Measles Outbreak - SNNPR"
                date="Oct 28, 2025"
                status="Resolved"
                severity="medium"
              />
            </div>
          </WireframeCard>
        )}
      </WireframeContent>

      {/* Create Alert Modal */}
      <CreateAlertModal
        open={showCreateAlert}
        onClose={() => setShowCreateAlert(false)}
        onCreated={(alert) => {
          setAlerts(prev => [alert, ...prev]);
          setShowCreateAlert(false);
          addToast({ type: 'success', title: 'Alert created', message: `${alert.title} has been added to the alert feed.` });
        }}
      />

      <style>{`
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(2); opacity: 0; }
        }
      `}</style>
    </WireframeLayout>
  );
}

// Alert Rule Item Component
function AlertRuleItem({ name, description, enabled }: { name: string; description: string; enabled: boolean }) {
  const [isEnabled, setIsEnabled] = useState(enabled);

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200">
      <div>
        <div className="font-medium text-sm">{name}</div>
        <div className="text-xs text-gray-500 mt-0.5">{description}</div>
      </div>
      <button
        onClick={() => setIsEnabled(!isEnabled)}
        className={cn(
          'w-10 h-5 rounded-full relative transition-colors',
          isEnabled ? 'bg-green-600' : 'bg-gray-300'
        )}
      >
        <div
          className={cn(
            'absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform',
            isEnabled ? 'left-5' : 'left-0.5'
          )}
        />
      </button>
    </div>
  );
}

// History Item Component
function HistoryItem({ title, date, status, severity }: { title: string; date: string; status: string; severity: 'critical' | 'high' | 'medium' | 'low' }) {
  const statusColors = {
    Active: 'text-red-600',
    Monitoring: 'text-amber-600',
    Resolved: 'text-green-600',
  };

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200">
      <div className="flex items-center gap-3">
        <CrisisBadge severity={severity} />
        <div>
          <div className="font-medium text-sm">{title}</div>
          <div className="text-xs text-gray-500">{date}</div>
        </div>
      </div>
      <span className={cn('text-xs font-medium', statusColors[status as keyof typeof statusColors] || 'text-gray-600')}>
        {status}
      </span>
    </div>
  );
}

// Create Alert Modal Component
function CreateAlertModal({
  open,
  onClose,
  onCreated
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (alert: Alert) => void;
}) {
  const [formData, setFormData] = useState({
    title: '',
    type: 'cholera',
    severity: 'high',
    region: 'Somali',
    locations: '',
    description: '',
    source: 'Manual Entry',
  });
  const [creating, setCreating] = useState(false);
  const { addToast } = useToast();

  const handleSubmit = async () => {
    if (!formData.title || !formData.locations) {
      addToast({ type: 'warning', title: 'Please fill required fields' });
      return;
    }

    setCreating(true);
    try {
      const alert = await wireframeMockApi.createAlert({
        type: formData.type as Alert['type'],
        title: formData.title,
        severity: formData.severity as Alert['severity'],
        region: formData.region,
        locations: formData.locations,
        window: '48h',
        confidence: '75%',
        description: formData.description,
        source: formData.source,
      });
      onCreated(alert);
      // Reset form
      setFormData({
        title: '',
        type: 'cholera',
        severity: 'high',
        region: 'Somali',
        locations: '',
        description: '',
        source: 'Manual Entry',
      });
    } catch (error) {
      addToast({ type: 'error', title: 'Failed to create alert' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create Manual Alert"
      subtitle="Add a new crisis alert to the detection system"
      size="lg"
      footer={
        <>
          <button
            onClick={onClose}
            disabled={creating}
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={creating}
            className="px-4 py-2 text-sm font-medium bg-accent-500 text-white hover:bg-accent-600 flex items-center gap-2"
          >
            {creating && <LoadingSpinner size="sm" className="text-white" />}
            <Bell className="w-4 h-4" />
            Create Alert
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Alert Title"
          value={formData.title}
          onChange={(v) => setFormData(prev => ({ ...prev, title: v }))}
          placeholder="e.g., Cholera Outbreak"
          required
          className="sm:col-span-2"
        />

        <Select
          label="Crisis Type"
          value={formData.type}
          onChange={(v) => setFormData(prev => ({ ...prev, type: v }))}
          options={[
            { value: 'cholera', label: 'Cholera' },
            { value: 'flooding', label: 'Flooding' },
            { value: 'drought', label: 'Drought' },
            { value: 'conflict', label: 'Conflict' },
            { value: 'displacement', label: 'Displacement' },
          ]}
        />

        <Select
          label="Severity"
          value={formData.severity}
          onChange={(v) => setFormData(prev => ({ ...prev, severity: v }))}
          options={[
            { value: 'critical', label: 'Critical' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' },
          ]}
        />

        <Select
          label="Region"
          value={formData.region}
          onChange={(v) => setFormData(prev => ({ ...prev, region: v }))}
          options={regions.filter(r => r !== 'All Regions').map(r => ({ value: r, label: r }))}
        />

        <Input
          label="Affected Locations"
          value={formData.locations}
          onChange={(v) => setFormData(prev => ({ ...prev, locations: v }))}
          placeholder="e.g., Jijiga, Kebridehar"
          required
        />

        <TextArea
          label="Description"
          value={formData.description}
          onChange={(v) => setFormData(prev => ({ ...prev, description: v }))}
          placeholder="Describe the situation..."
          className="sm:col-span-2"
          rows={3}
        />

        <Input
          label="Source"
          value={formData.source}
          onChange={(v) => setFormData(prev => ({ ...prev, source: v }))}
          placeholder="e.g., Field Report, MOH"
        />
      </div>
    </Modal>
  );
}

export default WireframeDetection;
